import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthRoles, getUserGroups } from '../../api/bin';
import LoaderComponent from '../../components/LoaderComponent';
import { useSession } from '../SessionProvider/SessionProvider';
import UserNoAccess from '../../components/AuthZ/UserNoAccess';

interface AccessProviderValue {
  checkAccess: (scope: string | RegExp) => boolean;
  checkPathAccess: (path: string, perform?: string | RegExp) => boolean;
  checkUserGroupAccess: (groups: string[]) => boolean;
}

const AccessContext = React.createContext<AccessProviderValue>({
  checkAccess: (_: string | RegExp) => false,
  checkPathAccess: (_: string, __ = '') => false,
  checkUserGroupAccess: (_: string[]) => false,
});

export const AccessProvider: React.FC<any> = ({ children }) => {
  const { accessToken } = useSession();
  const [authFlag, setAuthFlag] = useState<boolean>(true);

  const { data: scopes, isLoading, status, refetch } = useQuery<string[]>(
    ['userPermissions'],
    () => getAuthRoles('fetchAuthRoles', accessToken),
    {
      enabled: authFlag,
      retry: false,
    },
  );

  const { data: groups, refetch: refetchGroup } = useQuery<string[]>(
    ['usergroups'],
    () => getUserGroups('fetchUserGroups'),
    {
      enabled: authFlag,
      retry: false,
    },
  );

  useEffect(() => {
    refetchGroup();
    setAuthFlag(false);
  }, [scopes]);

  useEffect(() => {
    refetch();
    refetchGroup();
  }, [accessToken, refetch, refetchGroup]);

  const checkAccess: AccessProviderValue['checkAccess'] = useCallback(
    (scope) =>
      Boolean(
        scopes?.some((s: any) => {
          if (scope) {
            scope = scope instanceof RegExp ? scope : new RegExp(scope);
            return (scope as RegExp).test(s);
          }
          return false;
        }),
      ),
    [scopes],
  );

  const checkPathAccess: AccessProviderValue['checkPathAccess'] = useCallback(
    (path, perform = '.(view|edit)') => {
      return Boolean(
        scopes?.some((s: any) => {
          let result = s.startsWith(path.substr(1).replace(/\//g, '.'));

          if (perform) {
            perform = perform instanceof RegExp ? perform : new RegExp(perform);
            result = result && (perform as RegExp).test(s);
          }

          return result;
        }),
      );
    },
    [scopes],
  );

  const checkUserGroupAccess: AccessProviderValue['checkUserGroupAccess'] = useCallback(
    (group) => Boolean(group?.some((s: any) => groups?.includes(s))),
    [groups],
  );

  return (
    <AccessContext.Provider
      value={{
        checkAccess,
        checkPathAccess,
        checkUserGroupAccess,
      }}
    >
      {status === 'success' ? (
        children
      ) : isLoading ? (
        <LoaderComponent height={'100vh'} />
      ) : (
        <UserNoAccess />
      )}
    </AccessContext.Provider>
  );
};

export const useAccess = () => React.useContext(AccessContext);
